/**
 * ServiceLogForm - Block D
 * 
 * Dialog for adding completed service log entries.
 * Uses POST /api/aircraft/{id}/service-log endpoint.
 * 
 * Fields:
 * - work_performed (required)
 * - parts_used (optional)
 * - labor_hours (optional)
 * - technician (optional)
 * - service_date (optional, defaults to now)
 */

import { useState } from 'react';
import { useLanguage } from '../../contexts/LanguageContext';
import api from '../../lib/api';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Loader2, Plus, X } from 'lucide-react';
import { toast } from 'sonner';

export const ServiceLogForm = ({ 
  isOpen, 
  onClose, 
  aircraftId,
  onSuccess 
}) => {
  const { t, language } = useLanguage();
  const [loading, setLoading] = useState(false);
  
  // Form state
  const [workPerformed, setWorkPerformed] = useState('');
  const [technician, setTechnician] = useState('');
  const [laborHours, setLaborHours] = useState('');
  const [serviceDate, setServiceDate] = useState('');
  const [parts, setParts] = useState([]);
  
  // New part input state
  const [newPartName, setNewPartName] = useState('');
  const [newPartQty, setNewPartQty] = useState('1');
  const [newPartNumber, setNewPartNumber] = useState('');
  
  const resetForm = () => {
    setWorkPerformed('');
    setTechnician('');
    setLaborHours('');
    setServiceDate('');
    setParts([]);
    setNewPartName('');
    setNewPartQty('1');
    setNewPartNumber('');
  };
  
  const handleClose = () => {
    resetForm();
    onClose();
  };
  
  const addPart = () => {
    if (!newPartName.trim()) return;
    
    setParts([
      ...parts,
      {
        name: newPartName.trim(),
        quantity: parseInt(newPartQty) || 1,
        part_number: newPartNumber.trim() || null
      }
    ]);
    
    setNewPartName('');
    setNewPartQty('1');
    setNewPartNumber('');
  };
  
  const removePart = (index) => {
    setParts(parts.filter((_, i) => i !== index));
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (workPerformed.length < 5) {
      toast.error(t.aircraftDetail?.workPerformedMinLength || 'Work description must be at least 5 characters');
      return;
    }
    
    setLoading(true);
    
    try {
      const payload = {
        work_performed: workPerformed,
        technician: technician || null,
        labor_hours: laborHours ? parseFloat(laborHours) : null,
        parts_used: parts.length > 0 ? parts : null,
        service_date: serviceDate ? new Date(serviceDate).toISOString() : null
      };
      
      await api.post(`/aircraft/${aircraftId}/service-log?lang=${language}`, payload);
      
      toast.success(t.aircraftDetail?.serviceLogAdded || 'Service log added');
      
      resetForm();
      onSuccess?.();
      onClose();
    } catch (err) {
      console.error('Failed to create service log:', err);
      const errorMsg = err.response?.data?.detail?.message_cs || 
                       err.response?.data?.detail?.message_en || 
                       t.aircraftDetail?.serviceLogFailed || 'Failed to add service log';
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };
  
  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>
            {t.aircraftDetail?.addRecord || 'Add Service Record'}
          </DialogTitle>
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Work Performed (required) */}
          <div className="space-y-2">
            <Label htmlFor="workPerformed">
              {t.aircraftDetail?.workPerformed || 'Work Performed'} *
            </Label>
            <Textarea
              id="workPerformed"
              value={workPerformed}
              onChange={(e) => setWorkPerformed(e.target.value)}
              placeholder={t.aircraftDetail?.workPerformedPlaceholder || 'Describe the work performed (min. 5 characters)...'}
              className="min-h-[100px]"
              required
              data-testid="service-log-work-performed"
            />
          </div>
          
          {/* Technician */}
          <div className="space-y-2">
            <Label htmlFor="technician">
              {t.aircraftDetail?.technician || 'Technician'}
            </Label>
            <Input
              id="technician"
              value={technician}
              onChange={(e) => setTechnician(e.target.value)}
              placeholder={t.aircraftDetail?.technicianPlaceholder || 'Technician name'}
              data-testid="service-log-technician"
            />
          </div>
          
          {/* Labor Hours */}
          <div className="space-y-2">
            <Label htmlFor="laborHours">
              {t.aircraftDetail?.laborHours || 'Labor Hours'}
            </Label>
            <Input
              id="laborHours"
              type="number"
              step="0.5"
              min="0"
              value={laborHours}
              onChange={(e) => setLaborHours(e.target.value)}
              placeholder="0.0"
              data-testid="service-log-labor-hours"
            />
          </div>
          
          {/* Service Date */}
          <div className="space-y-2">
            <Label htmlFor="serviceDate">
              {t.aircraftDetail?.serviceDate || 'Service Date'}
            </Label>
            <Input
              id="serviceDate"
              type="date"
              value={serviceDate}
              onChange={(e) => setServiceDate(e.target.value)}
              data-testid="service-log-date"
            />
            <p className="text-xs text-slate-500">
              {t.aircraftDetail?.serviceDateHint || 'Leave empty for current date'}
            </p>
          </div>
          
          {/* Parts Used */}
          <div className="space-y-2">
            <Label>
              {t.aircraftDetail?.partsUsed || 'Parts Used'}
            </Label>
            
            {/* Parts list */}
            {parts.length > 0 && (
              <ul className="space-y-1 mb-2">
                {parts.map((part, idx) => (
                  <li key={idx} className="flex items-center gap-2 text-sm bg-slate-50 dark:bg-slate-800 rounded px-2 py-1">
                    <span className="flex-1">
                      {part.name} x{part.quantity}
                      {part.part_number && <span className="text-slate-500 ml-1">({part.part_number})</span>}
                    </span>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="h-6 w-6 p-0"
                      onClick={() => removePart(idx)}
                    >
                      <X className="h-3 w-3" />
                    </Button>
                  </li>
                ))}
              </ul>
            )}
            
            {/* Add part inputs */}
            <div className="flex gap-2">
              <Input
                value={newPartName}
                onChange={(e) => setNewPartName(e.target.value)}
                placeholder={t.aircraftDetail?.partNamePlaceholder || 'Part name'}
                className="flex-1"
              />
              <Input
                type="number"
                min="1"
                value={newPartQty}
                onChange={(e) => setNewPartQty(e.target.value)}
                className="w-16"
                placeholder="Qty"
              />
              <Input
                value={newPartNumber}
                onChange={(e) => setNewPartNumber(e.target.value)}
                placeholder={t.aircraftDetail?.partNumberPlaceholder || 'Part #'}
                className="w-24"
              />
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={addPart}
                disabled={!newPartName.trim()}
              >
                <Plus className="h-4 w-4" />
              </Button>
            </div>
          </div>
          
          <DialogFooter>
            <Button type="button" variant="outline" onClick={handleClose}>
              {t.aircraftDetail?.cancel || 'Cancel'}
            </Button>
            <Button 
              type="submit" 
              disabled={loading || workPerformed.length < 5}
              data-testid="service-log-submit"
            >
              {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {t.aircraftDetail?.save || 'Save'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default ServiceLogForm;
